import { useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { staffApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { fieldErrorMap, fieldIssuesFromError } from '../../lib/apiFieldErrors.js';
import type { AdminStaff } from '../../api/types.js';
import type { StaffRole } from '@equinox/shared';
import { Modal } from '../../components/Modal.js';
import { PercentInput } from '../../components/PercentInput.js';
import {
  STAFF_PIN_PATTERN,
  isValidDiscountCeiling,
  isValidStaffPassword,
  isValidStaffPin,
} from '../../validation/staffLimits.js';

export interface StaffFormModalProps {
  staff: AdminStaff | null;
  onClose(): void;
  onSaved(staff: AdminStaff): void;
}

export function StaffFormModal({ staff, onClose, onSaved }: StaffFormModalProps) {
  const { t } = useI18n();
  const isNew = staff === null;
  const [code, setCode] = useState(staff?.code ?? '');
  const [displayName, setDisplayName] = useState(staff?.displayName ?? '');
  const [email, setEmail] = useState(staff?.email ?? '');
  const [role, setRole] = useState<StaffRole>(staff?.role ?? 'staff');
  const [ceiling, setCeiling] = useState(staff?.discountCeilingPct ?? 0);
  const [ceilingValid, setCeilingValid] = useState(true);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const pinValid = !isNew || STAFF_PIN_PATTERN.test(pin);
  const pinsMatch = !isNew || pin === pinConfirm;
  const passwordRequired = isNew && role === 'admin';
  // Mirrors the server's own CreateBody exactly (min length 12 — see
  // packages/server/src/routes/staff.ts and
  // packages/admin/src/validation/staffLimits.ts). This used to check
  // `password.trim().length >= 8`, eight short of what the server actually
  // requires, so an 11-character password passed here and 400'd on the
  // server with nothing but "request failed validation" to show for it
  // (bug 1).
  const passwordOk = !passwordRequired || isValidStaffPassword(password);
  const canSave =
    code.trim() &&
    displayName.trim() &&
    pinValid &&
    pinsMatch &&
    passwordOk &&
    ceilingValid &&
    isValidDiscountCeiling(ceiling) &&
    !saving;

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const saved = isNew
        ? await staffApi.create({
            code: code.trim(),
            displayName: displayName.trim(),
            email: email.trim() || null,
            role,
            discountCeilingPct: ceiling,
            pin,
            password: role === 'admin' ? password : undefined,
          })
        : await staffApi.update(staff.id, {
            displayName: displayName.trim(),
            email: email.trim() || null,
            discountCeilingPct: ceiling,
          });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('staff.codeInUse'));
      } else {
        const issues = fieldIssuesFromError(err);
        if (issues.length > 0) {
          setFieldErrors(fieldErrorMap(t, issues));
          setError(t('error.validation'));
        } else {
          setError(t('common.error'));
        }
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isNew ? t('staff.new') : t('common.edit')} onClose={onClose} busy={saving}>
      {error && <p className="field-error">{error}</p>}

      <label htmlFor="staff-code">
        {t('staff.code')}
        <input
          id="staff-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={!isNew || saving}
          required
        />
        {fieldErrors.code && <span className="field-error">{fieldErrors.code}</span>}
      </label>

      <label htmlFor="staff-name">
        {t('staff.displayName')}
        <input
          id="staff-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={saving}
          required
        />
        {fieldErrors.displayName && <span className="field-error">{fieldErrors.displayName}</span>}
      </label>

      <label htmlFor="staff-role">
        {t('staff.role')}
        <select
          id="staff-role"
          value={role}
          onChange={(e) => setRole(e.target.value as StaffRole)}
          disabled={!isNew || saving}
        >
          <option value="staff">{t('staff.roleStaff')}</option>
          <option value="admin">{t('staff.roleAdmin')}</option>
        </select>
      </label>

      {role === 'admin' && (
        <label htmlFor="staff-email">
          {t('staff.email')}
          <input id="staff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={saving} />
          {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
        </label>
      )}

      <PercentInput
        id="staff-ceiling"
        label={t('staff.discountCeiling')}
        value={ceiling}
        onChange={(value, valid) => {
          setCeiling(value);
          setCeilingValid(valid);
        }}
        hint={t('staff.discountCeilingHelp')}
        errorMessage={fieldErrors.discountCeilingPct ?? t('staff.discountCeilingInvalid')}
        disabled={saving}
      />

      {isNew && (
        <>
          <label htmlFor="staff-pin">
            {t('staff.pin')}
            <input
              id="staff-pin"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={saving}
            />
            {pin && !isValidStaffPin(pin) && <span className="field-error">{t('staff.pinInvalid')}</span>}
            {fieldErrors.pin && <span className="field-error">{fieldErrors.pin}</span>}
          </label>
          <label htmlFor="staff-pin-confirm">
            {t('staff.pinConfirm')}
            <input
              id="staff-pin-confirm"
              type="password"
              inputMode="numeric"
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value)}
              disabled={saving}
            />
            {pinConfirm && pin !== pinConfirm && <span className="field-error">{t('staff.pinMismatch')}</span>}
          </label>
          <p className="field-hint">{t('staff.pinHashedNotice')}</p>

          {role === 'admin' && (
            // Its own `.field` div rather than a wrapping <label>: the hint/
            // error text below is always present (one branch or another
            // always renders), so if it sat inside the <label> it would
            // permanently fold into the label's accessible name and break
            // `getByLabelText('Contraseña de administrador')` — the same
            // reasoning as MoneyInput/PercentInput's affix symbol.
            <div className="field">
              <label htmlFor="staff-password">{t('staff.password')}</label>
              <input
                id="staff-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPasswordTouched(true)}
                disabled={saving}
                aria-invalid={passwordTouched && !passwordOk}
              />
              {passwordTouched && password.trim() === '' ? (
                <span className="field-error">{t('staff.passwordRequiredForAdmin')}</span>
              ) : passwordTouched && !passwordOk ? (
                <span className="field-error">{t('staff.passwordTooShort')}</span>
              ) : (
                <>
                  <span className="field-hint">{t('staff.passwordHelp')}</span>
                  <span className="field-hint">{t('staff.passwordMinLength')}</span>
                </>
              )}
              {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
            </div>
          )}
        </>
      )}

      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!canSave} onClick={() => void submit()}>
          {saving ? t('common.loading') : t('staff.save')}
        </button>
      </div>
    </Modal>
  );
}
